/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import ErrorModule from 'app/mixins/ErrorModule.jsx';
import DropDown from 'app/components/common/DropDown.jsx';
import SearchControl from 'app/components/common/SearchControl.jsx';
import FeedItem from 'app/components/DropDownItems/FeedItem.jsx';
import SocialButtonConnect from 'app/components/common/SocialButtonConnect.jsx';
import * as IconFactory from 'app/components/common/IconFactory.jsx';
import SelectedFeedItem from 'app/components/common/SelectedFeedItem.jsx';
import Loading from 'app/components/common/Loading.jsx';
import FeedSearchResult from 'app/components/CreateFeed/FeedSearchResult.jsx';
import StyledSelect from 'app/components/common/StyledSelect.jsx';
import * as _ from 'underscore';
import * as appConstants from 'app/redux/constants.js';
import { Breakpoint } from 'react-socks';
import SuggestedFeeds from 'app/components/CreateFeed/SuggestedFeeds.jsx';
import {
	fetchSuggestedFeedCategories,
	fetchSuggestedFeeds
} from 'app/modules/SuggestedFeedData.js';
import { feedCadence } from 'app/utility/SuggestFeeds.js';
import { FindStep } from '@/shadcn/components/NewAutomation/FindStep.tsx';

const errorMap = (errors, errorText, props, key, state) => {
	// Safety check, if errorText isn't a string it won't have a match method so just return and empty string
	// probably in a weird case at this point anyhow and the error being reported isn't fully baked or an actual blocker
	if (!_.isString(errorText)) {
		return '';
	}

	if (errorText.match('Content is an HTML Document, not an RSS/Atom feed')) {
		return "We can't find a feed on this web page (This is an HTML page not a RSS / Atom feed)";
	} else if (errorText.match('Invalid Url (404)')) {
		return "There's no feed at this address (HTTP 404)";
	} else if (errorText.match('This feed is bigger')) {
		let upgradeLink = null;
		if (props.user.detail.pubLevel <= 1) {
			upgradeLink = (<a href="/account/subscription/compare" className="error-link">{"Check out dlvr.it's premium plans."}</a>);
		}
		return (
			<span key={key}>
				<span>{errorText} {upgradeLink}</span>
			</span>
		);
	} else if (errorText.match("We can't find a working feed")) {
		if (errors.suggestedFeeds !== undefined && errors.suggestedFeeds.feeds !== undefined && errors.suggestedFeeds.feeds.length) {
			return (
				<span key={key}>
					{errorText}. <span className="additional-info">Please try another search or choose a related feed below.</span>
				</span>
			);
		}
	} else if (errorText.match("POSSIBLE SOCIAL SEARCH")) {
		return (
			<span key={key} className="additional-info">
				Want to share from a social profile? You can do that <a href="/share/automate/new/inputs?social=true">here</a>
			</span>
		);
	} else if (errorText.match("USER SEARCH")) {
		let hasSuggestedFeeds = false;
		if (props.app.errors.api.length) {
			for (let i = 0; i < props.app.errors.api.length; ++i) {
				let e = props.app.errors.api[i];
				if (e.errors !== undefined &&
					e.errors.suggestedFeeds !== undefined &&
					e.errors.suggestedFeeds.feeds !== undefined &&
					e.errors.suggestedFeeds.feeds.length) {
					hasSuggestedFeeds = true;
				}
			}
		}
		if (hasSuggestedFeeds || props.app.errors.api.length === 1) {
			return (
				<span key={key}>
					<span className="search-echo">"{state.inputContent}"</span> We can’t find a working feed there. <br/><span className="additional-info">Please try another search or choose a related feed below. <br/>Want to share from a social profile? You can do that <a href="/share/automate/new/inputs?social=true">here</a></span>
				</span>
			);
		} else if (props.feeds.searchResults.feeds.length) {
			return (
				<span key={key}>
					<span className="additional-info">Please try another search or choose a related feed below. <br/>Want to share from a social profile? You can do that <a href="/share/automate/new/inputs?social=true">here</a></span>
				</span>
			);
		} else {
			return (
				<span key={key}>
					<span className="additional-info">Want to share from a social profile? You can do that <a href="/share/automate/new/inputs?social=true">here</a></span>
				</span>
			);
		}

	} else if (errorText.match("YOUTUBE")) {
		return (
			<span key={key}>
				<span className="additional-info">Want to share YouTube videos? Select the YouTube option <a href="/share/automate/new/inputs">here</a></span>
			</span>
		);
	} else if (errorText.match(/cloudflare/i)) {
		return (
			<span key={key}>
				Cloudflare is restricting access to this feed. <a href="https://help.dlvrit.com/en/articles/14862214-cloudflare-allow-dlvr-it-to-access-your-rss-feeds" target="_blank">View details</a> on enabling access.
			</span>
		);
	}

	return errorText;
};

/*
 * Search languages, as prod offers them. Hoisted off the render so the new find
 * step and the legacy select read one list — the step names a language and this
 * is what turns that back into the code feedSearch wants.
 */
const LANGUAGE_OPTIONS = [
	{value: 'en', label: 'English'},
	{value: 'es', label: 'Español'},
	{value: 'fr', label: 'Français'},
	{value: 'it', label: 'Italiano'},
	{value: 'ja', label: '日本語'},
	{value: 'pt', label: 'Português'},
	{value: 'de', label: 'Deutsch'},
	{value: 'nl', label: 'Nederlands'},
	{value: 'ru', label: 'Русский'},
	{value: 'zh', label: '中文'}
];

/* SearchControl's own typing debounce, reproduced for the new step's field. */
const SEARCH_DEBOUNCE_MS = 1200;

class CreateFeed extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			inputContent: '',
			searchResults: [],
			currentPage: 1,
			defaultFocus: true,
			searchQuery: '',
			externalFeedUrl: '',
			externalFeedSearch: false,
			selectExternalFeed: false,
			searchLanguage: 'en',
			isSearching: false,
			activeTab: 'rss',
			// new find step only
			tileQuery: '',
			suggestedCategories: [],
			suggestedFeeds: [],
			tileCategory: null,
			/*
				Only the tile find step reads these. The catalogue flag starts true
				because its two fetches begin in componentDidMount, so the first
				render is already waiting on them — starting false would flash the
				empty state for one frame.
			*/
			tileSuggestionsLoading: props.tile === true,
			tileSearchPending: false
		};

		this.tileDebounce = 0;

		this.triggerExternalSearch = this.triggerExternalSearch.bind(this);
		this.inputHandler = this.inputHandler.bind(this);
		this.focusHandler = this.focusHandler.bind(this);
		this.selectFeed = this.selectFeed.bind(this);
		this.removeFeed = this.removeFeed.bind(this);
		this.searchSuggestion = this.searchSuggestion.bind(this);
		this.updateSearchLanguage = this.updateSearchLanguage.bind(this);
		this.checkForSocialSearch = this.checkForSocialSearch.bind(this);
		this.onTabClick = this.onTabClick.bind(this);
		this.onTileQueryChange = this.onTileQueryChange.bind(this);
		this.onTileLanguageChange = this.onTileLanguageChange.bind(this);
		this.onTileCategoryChange = this.onTileCategoryChange.bind(this);
		this.onTileConnect = this.onTileConnect.bind(this);
		this.onTilePreview = this.onTilePreview.bind(this);

	}

	componentDidMount() {

		// clean up at mount
		this.props.resetFeedSearch();
		this.props.resetFeedValidation();

		if (this.props.externalFeedUrl !== '') {
			this.setState({
				externalFeedUrl: this.props.externalFeedUrl,
				externalFeedSearch: true
			}, () => {
				this.triggerExternalSearch();
			});
		}

		if (this.props.searchQuery !== '') {
			this.setState({
				searchQuery: this.props.searchQuery
			});
		}

		/*
		 * The new find step shows the suggested-feed catalogue inline rather than
		 * behind an "Explore Feeds" tab, so it needs the categories up front.
		 * SuggestedFeeds fetches the same two lists for the legacy tab.
		 */
		if (this.props.tile) {
			/*
				The list needs BOTH lists — the categories pick which feeds show —
				so it waits for the second to land rather than rendering against a
				half-loaded catalogue. Counted down on failure too: a rejected fetch
				has finished, and leaving the flag set would show skeletons forever.
			*/
			let outstanding = 2,
				settled = () => {
					outstanding -= 1;
					if (outstanding === 0) {
						this.setState({tileSuggestionsLoading: false});
					}
				};

			fetchSuggestedFeedCategories(this.props.app.config.appUrl)
				.then((categories) => {
					this.setState({
						suggestedCategories: categories,
						tileCategory: (this.state.tileCategory === null && categories.length)
							? categories[0].dsfc_id
							: this.state.tileCategory
					});
				})
				.catch(() => {
					// leaving the categories empty just hides the strip
				})
				.then(settled);

			fetchSuggestedFeeds(this.props.app.config.appUrl)
				.then((feeds) => {
					this.setState({suggestedFeeds: feeds});
				})
				.catch(() => {
					// no suggestions; search still works
				})
				.then(settled);
		}

	}

	componentWillUnmount() {

		let onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		/*
			The find step's search is debounced by over a second, so navigating
			straight after typing left a timer that fired into a dead component:
			a setState plus a stray feedSearch dispatch.
		*/
		window.clearTimeout(this.tileDebounce);
		let eventData = {
			onboarding: onboarding !== null
		};
		if (onboarding !== null) {
			if (onboarding.searchAttempts !== undefined) {
				eventData.searchAttempts = onboarding.searchAttempts;
			}
		}

		if (this.props.recordEvent) {
			this.props.recordEvent({
				pub_id: this.props.user.detail.id,
				event_name: 'Automation Building - Feed Search Attempts',
				event_data: eventData
			});
		}

		// clear out validation errors on teardown
		this.props.deleteApiError(['INPUT_VALIDATE', 'FEED_SEARCH', 'FEED_VALIDATE']);

	}

	static getDerivedStateFromProps(props, state) {
		let nextState = {};

		if (props.feeds && props.feeds.searchResults.length && state.selectExternalFeed) {
			nextState.selectExternalFeed = false;
			nextState.addExternalFeed = true;
		}

		if (props.user && props.user.perms && props.user.perms.resources.inputsAvailable === 0) {
			nextState.defaultFocus = false;
		}

		return Object.keys(nextState).length ? nextState : null;

	}

	componentDidUpdate(prevProps, prevState, snapshot) {

		if (this.state.addExternalFeed) {
			this.setState({
				addExternalFeed: false,
			});
			if (this.state.searchResults.length) {
				this.selectFeed(this.state.searchResults[0]);
			}
		}

		if (prevProps.app.loading.FEED_SEARCH === true && this.props.app.loading.FEED_SEARCH === false) {
			this.checkForSocialSearch(this.state.inputContent);
			this.setState({
				isSearching: false,
				// A response makes any queued keystroke's wait moot.
				tileSearchPending: false
			});
		}

	}

	triggerExternalSearch() {

		this.inputHandler(null, this.state.externalFeedUrl);

		this.setState({
			externalFeedSearch: false,
			externalFeedUrl: '',
			selectExternalFeed: true
		});

	}

	checkForSocialSearch(query) {
		let message = null,
			errors = [];

		// fake a feed search error to help redirect people to the
		// input tiles if it looks like they're trying to add a social
		errors = this.props.getApiErrors(['FEED_SEARCH']);
		if (_.isString(query) && errors.length) {
			if (query.match(/facebook|twitter|instagram/i)) {
				message = "POSSIBLE SOCIAL SEARCH";
			} else if (query.match(/\@[\w+]/)) {
				message = "USER SEARCH";
			} else if (query.match(/youtube/i)) {
				message = "YOUTUBE";
			}

			if (message) {
				this.props.setApiError({
					type: 'FEED_SEARCH',
					error: {
						errors: {
							validation: {
								query: message
							}
						}
					}
				});
			}
		}
	}

	inputHandler(event, newValue){
		let searchResults = [],
			onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		if (this.props.canAddFeeds === false) {
			if (newValue === '') {
				this.props.resetFeedSearch();
				if (this.props.removeInvalidFeeds) {
					this.props.removeInvalidFeeds();
				}
			}
			return;
		}

		// debounce (kinda) search requests - prevent a new request from firing if it's the same
		if (this.state.isSearching && newValue === this.state.inputContent) {
			return;
		}

		newValue = newValue.trim();

		// for mobile devices that like to capitalize the first letter - which would blow up a direct feed request
		// We cannot use .toLowerCase() because that modifies the entire URL and blows up many, many searches
		if (newValue.match(/^http/i)) {
			newValue = newValue.replace(/^http/i, 'http');
		}

		if (newValue !== '') {
			this.props.deleteApiError(['FEED_VALIDATE', 'INPUT_VALIDATE']);
			this.props.feedSearch(newValue, this.state.searchLanguage);

			if (onboarding !== null) {
				onboarding.searchAttempts++;
				window.localStorage.setItem('onboarding', JSON.stringify(onboarding));
			}

			if (this.props.onboarding !== null) {
				dataLayer.push({
					'event': 'onboarding-trigger-search',
					'search-term': newValue
				});

				if (this.props.recordEvent) {
					this.props.recordEvent({
						pub_id: this.props.user.detail.id,
						event_name: 'Automation Building - Trigger Search',
						event_data: {
							'term': newValue,
							'onboarding': onboarding !== null
						}
					});
				}
			}
		} else if (newValue === '') {
			this.props.deleteApiError(['FEED_VALIDATE', 'INPUT_VALIDATE', 'FEED_SEARCH']);
			this.props.resetFeedSearch();
			// Feed search is being reset remove any invalid feeds
			if (this.props.removeInvalidFeeds) {
				this.props.removeInvalidFeeds();
			}
		}

		this.setState({
			inputContent: newValue,
			currentPage: 1,
			searchQuery: '',
			isSearching: true
		});
	}

	focusHandler(e) {

		let trigger = (this.props.context === 'curate') ? 'curate' : 'automate';

		if (this.props.user.perms.resources.inputsAvailable === 0) {
			e.target.blur();
			this.props.showUpgradeModal(this.props.closeAllModals, 'inputs', null, trigger + '_create');
		} else {
			if (dataLayer) {
				if (this.props.onboarding !== null) {
					if (_.findWhere(dataLayer, {'event': 'onboarding-start-search'}) === undefined) {
						dataLayer.push({
							'event': 'onboarding-start-search'
						});
					}
				}
			}
		}

	}

	selectFeed(feed){
		this.setState({
			inputContent: '',
			searchResults: []
		});

		feed.type = this.props.app.config.constants.INPUT_TYPE_FEED;
		feed.name = feed.title;

		this.props.resetFeedSearch();

		this.props.onFeedSelect(feed);

		let onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		if (onboarding !== null) {
			if (onboarding.searchAttempts !== undefined) {
				if (this.props.recordEvent) {
					this.props.recordEvent({
						pub_id: this.props.user.detail.id,
						event_name: 'Automation Building - Feed Selected',
						event_data: {
							'feed': feed,
							'onboarding': onboarding !== null
						}
					});
				}
			}

		}

	}

	removeFeed(event) {
		let feedId = event.currentTarget.dataset.feedid;
		this.props.deleteApiError([appConstants.FEED_SEARCH, appConstants.FEED_VALIDATE]);
		this.props.onFeedRemoval(feedId);
	}

	searchSuggestion(e) {
		let suggestion = null,
			onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		if (e) {
			e.preventDefault();
		}

		if (e.currentTarget.dataset.tag !== undefined) {
			suggestion = e.currentTarget.dataset.tag;
			window.scrollTo(0, 0);

			if (this.props.recordEvent && onboarding) {
				this.props.recordEvent({
					pub_id: this.props.user.detail.id,
					event_name: 'Topic Search Clicked',
					event_data: {
						topic: suggestion
					}
				});
			}
		}

		if (suggestion) {
			this.setState({
				searchQuery: suggestion
			});
		}

	}

	updateSearchLanguage(e) {
		let onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		if (this.props.recordEvent && onboarding) {
			this.props.recordEvent({
				pub_id: this.props.user.detail.id,
				event_name: 'Search Language Changed',
				event_data: {
					locale: e.currentTarget.value
				}
			});
		}

		this.setState({
			searchLanguage: e.currentTarget.value,
			isSearching: false
		}, function(){
			this.inputHandler(null, this.state.inputContent);
		});

	}

	onTabClick(e) {
		let tab = e.currentTarget.dataset.tab;

		this.setState({
			activeTab: tab
		});

	}


	/*
	 * ── the new find step ────────────────────────────────────────────────────
	 *
	 * The design puts on one screen what prod splits across two tabs: the search
	 * field, and the suggested-feed catalogue that used to live behind "Explore
	 * Feeds". Searching hides the category strip, which is how the harness draws
	 * the searching state too.
	 *
	 * Not carried over, because the design has neither: the results pager and the
	 * "Related searches" tag list. Both only appear on a search with more results
	 * than fit, so nothing is unreachable without them — but they ARE a loss for
	 * a broad query, and worth raising with the designer.
	 */

	/* Both sources reduced to the one shape selectFeed and FeedSearchResult use. */
	normalizeFeed(raw) {
		if (raw.feed_url !== undefined) {
			return {
				visualUrl: raw.feed_icon,
				description: raw.feed_description,
				title: raw.feed_title,
				url: raw.feed_url,
				// DlvrSuggestedFeeds.inputVelocity, refreshed by the daily
				// updateSuggestedFeedVelocity cron. Search results have no
				// equivalent — nothing has parsed those feeds yet — so their rows
				// carry no cadence, which feedCadence renders as nothing.
				inputVelocity: raw.inputVelocity
			};
		}

		return raw;
	}

	toFeedResult(feed) {
		return {
			id: feed.url,
			name: feed.title,
			description: feed.description,
			// '' at the velocity floor, which is also what a feed with no history
			// reports — see feedCadence. undefined so the badge is simply absent
			// rather than an empty pill.
			velocity: feedCadence(feed.inputVelocity) || undefined,
			iconUrl: feed.visualUrl || undefined,
			monogram: String(feed.title || '?').trim().charAt(0).toUpperCase(),
			linkLabel: 'Preview',
			connectLabel: 'Connect'
		};
	}

	/* A FeedResult carries everything selectFeed needs, so nothing is stashed. */
	feedFromResult(result) {
		return {
			url: result.id,
			title: result.name,
			description: result.description,
			visualUrl: result.iconUrl || ''
		};
	}

	onTileQueryChange(value) {
		/*
			Pending from the keystroke, not from the request: for SEARCH_DEBOUNCE_MS
			the query is non-empty while the results still belong to the previous
			one, and reporting that as "no feeds found" is how an in-flight search
			reads as a failed search.

			The timer clears it rather than the response doing so — the timer always
			fires, whereas inputHandler can decline to search (it short-circuits a
			repeat of a query already in flight). From that point `isSearching`
			carries the wait.
		*/
		this.setState({
			tileQuery: value,
			tileSearchPending: value.trim() !== ''
		});

		window.clearTimeout(this.tileDebounce);
		this.tileDebounce = window.setTimeout(() => {
			this.setState({tileSearchPending: false});
			this.inputHandler(null, value);
		}, SEARCH_DEBOUNCE_MS);
	}

	onTileLanguageChange(name) {
		let option = LANGUAGE_OPTIONS.find((candidate) => { return candidate.label === name; });

		if (option === undefined) {
			return;
		}

		this.updateSearchLanguage({currentTarget: {value: option.value}});
	}

	onTileCategoryChange(name) {
		let category = this.state.suggestedCategories.find((candidate) => {
			return candidate.category === name;
		});

		if (category === undefined) {
			return;
		}

		this.setState({tileCategory: category.dsfc_id});

		if (this.props.recordEvent) {
			this.props.recordEvent({
				pub_id: this.props.user.detail.id,
				event_name: 'Suggested Feed Category Click',
				event_data: {
					category: category.category
				}
			});
		}
	}

	onTileConnect(result) {
		this.selectFeed(this.feedFromResult(result));
	}

	onTilePreview(result) {
		window.open(`/feedPreview.php?url=${encodeURIComponent(result.id)}`, '_blank');
	}

	renderFindStep() {
		let searching = this.state.tileQuery.trim() !== '',
			language = LANGUAGE_OPTIONS.find((option) => {
				return option.value === this.state.searchLanguage;
			}),
			categoryNames = this.state.suggestedCategories.map((category) => {
				return category.category;
			}),
			activeCategory = this.state.suggestedCategories.find((category) => {
				return category.dsfc_id === this.state.tileCategory;
			}),
			results = [];

		if (searching) {
			/* isError rows carry a message rather than a feed, and the design's
			   row has no error treatment — the search error block above covers
			   them. */
			results = [].concat(this.props.feeds.searchResults.feeds)
				.filter((feed) => { return feed && !feed.isError; })
				.map((feed) => { return this.toFeedResult(this.normalizeFeed(feed)); });
		} else if (activeCategory !== undefined) {
			results = this.state.suggestedFeeds
				.filter((feed) => { return feed.dsfc_id === activeCategory.dsfc_id; })
				.map((feed) => { return this.toFeedResult(this.normalizeFeed(feed)); });
		}

		return (
			<FindStep
				findTitle={(this.props.inputType === 'wordpress')
					? 'Find your WordPress site'
					: 'Find a feed'}
				query={this.state.tileQuery}
				language={(language !== undefined) ? language.label : 'English'}
				languages={LANGUAGE_OPTIONS.map((option) => { return option.label; })}
				categories={categoryNames}
				activeCategory={(activeCategory !== undefined) ? activeCategory.category : ''}
				resultsCaption={(!searching && activeCategory !== undefined)
					? `Popular in ${activeCategory.category}`
					: undefined}
				results={results}
				searching={searching}
				/*
				 * `isValidating` is part of the wait, not a separate state. Picking
				 * a feed calls resetFeedSearch and then leaves this step MOUNTED
				 * for the whole FEED_VALIDATE round trip — so the results are
				 * already gone while the query still says a search is on, and the
				 * step would report "No feeds found" for the length of a network
				 * call. The legacy layout showed "Validating Feed" here.
				 */
				loading={this.props.isValidating
					|| (searching
						? (this.state.tileSearchPending || this.state.isSearching)
						: this.state.tileSuggestionsLoading)}
				loadingLabel={(this.props.isValidating) ? 'Validating feed' : undefined}
				/*
				 * The catalogue is what the user has just finished with, so the
				 * whole suggested block goes while their pick is checked — the
				 * skeleton rows carry the wait on their own.
				 */
				validating={this.props.isValidating}
				onQueryChange={this.onTileQueryChange}
				onLanguageChange={this.onTileLanguageChange}
				onCategoryChange={this.onTileCategoryChange}
				onConnect={this.onTileConnect}
				onPreview={this.onTilePreview} />
		);
	}

	render() {
		let searchResults = null,
			selectedFeeds = null,
			feedLabel = '',
			constants = this.props.app.config.constants,
			editButton = null,
			loading = null,
			pagerControl = null,
			currentResults = [],
			searchError = null,
			assetsUrl = this.props.app.config.assetsUrl,
			suggestion = null,
			searchQuery = this.state.searchQuery,
			facebookButton = null,
			twitterButton = null,
			twistoutCopy = '',
			twistoutButton = null,
			socialButtons = null,
			languageOptions = [
				{value: 'en', label: 'English'},
				{value: 'es', label: 'Español'},
				{value: 'fr', label: 'Français'},
				{value: 'it', label: 'Italiano'},
				{value: 'ja', label: '日本語'},
				{value: 'pt', label: 'Português'},
				{value: 'de', label: 'Deutsch'},
				{value: 'nl', label: 'Nederlands'},
				{value: 'ru', label: 'Русский'},
				{value: 'zh', label: '中文'}
			],
			socialInputs = null,
			searchWrapperClass = 'feed',
			languageClass = 'language-select',
			searchTags = null,
			resultList = [],
			titleCopy = 'Post from RSS feed, site, or blog.',
			placeholderCopy = 'Enter RSS Feed URL, site, or topic',
			languageSelector = null,
			imgPath = this.props.app.config.assetsUrl + "/" + this.props.app.config.imageUrl;


		if (this.props.inputType === 'wordpress') {
			titleCopy = 'Auto post items from a WordPress site';
			placeholderCopy = 'Enter your WordPress site';
			searchWrapperClass += " wordpress";
		} else {
			languageSelector = (
				<StyledSelect
					options={languageOptions}
					value={this.state.searchLanguage}
					onChange={this.updateSearchLanguage}
					selectClass={languageClass}/>
			);
		}

		searchError = (
			<ErrorModule
				app={this.props.app}
				keys={[appConstants.FEED_SEARCH, appConstants.FEED_VALIDATE, appConstants.INPUT_VALIDATE, appConstants.ROUTE_CREATE, appConstants.CURATION_ROUTE_CREATE]}
				render={(state) => {
					if (state.errors.length) {
						//error.errors.suggestedFeeds.query
						return (
							<div className="feed-search-error">
								{state.errors.map((error, i) => {
									if (error.errors === undefined || error.errors.validation === undefined) {
										return null;
									}
									return (
										<div key={"feed_error_" + i}>
											{_.values(error.errors.validation).map((errorText, i) => {
												let key = "error_text_" + i;
												if (error.errors.suggestedFeeds !== undefined && error.errors.suggestedFeeds.query !== undefined) {
													return (
														<span key={key}>
															<span className="search-echo">"{error.errors.suggestedFeeds.query}"</span> {errorMap(error.errors, errorText, this.props, key, this.state)}
														</span>
													);
												} else {
													return errorMap(error.errors, errorText, this.props, key, this.state);
												}
											})}
										</div>
									);
								})}
							</div>
						);
					}
					return null;
				}}/>
		);

		/* The new find step draws its own layout; only the error block, which
		   it has no slot for, is kept above it. */
		if (this.props.tile) {
			return (
				<div>
					{(this.props.initialFeeds.length) ? null : searchError}
					{this.renderFindStep()}
				</div>
			);
		}

		if (this.props.isValidating && this.props.user.detail.routeViewType === constants.ROUTE_VIEW_SIMPLE) {
			selectedFeeds = (
				<Loading label="Validating Feed"/>
			);
		} else if (this.props.initialFeeds.length) {
			feedLabel = (this.props.initialFeeds.length > 1) ? 'Feeds' : 'Feed';
			selectedFeeds = (
				<div>
					{this.props.user.detail.routeViewType === constants.ROUTE_VIEW_ADVANCED ? (
						<p className="editor-section-heading">Selected {feedLabel}</p>
					) : null }
					<ul className="dlvr-feed-list">
						{this.props.initialFeeds.map(function(feed, i){
							let isCurated = (this.props.context === 'curate') ? true : false;

							return (
								<SelectedFeedItem
									{...this.props}
									key={i}
									feed={feed}
									onAdvancedFeedConfigure={this.props.onAdvancedFeedConfigure}
									removeFeed={this.removeFeed}
									isCurated={isCurated}
									errorMap={errorMap} />
							);
						}, this)}
					</ul>
				</div>
			);
		}

		if (this.props.app.loading.FEED_SEARCH) {
			searchResults = (
				<Loading label="Searching"/>
			);
		} else {
			if (this.props.feeds.searchResults.tags.length) {
				searchTags = (
					<div className="related-tags">
						<h3>Related searches</h3>
						<ul className="search-tags-list clearfix">
							{this.props.feeds.searchResults.tags.map((tag, i) => {
								return (
									<li key={i} className="search-tag" onClick={this.searchSuggestion} data-tag={tag} role="button" aria-label="search by tag">
										{tag}
									</li>
								);
							})}
						</ul>
					</div>
				);
			}

			let searchResultList = [].concat(this.props.feeds.searchResults.feeds);

			if (this.props.searchErrors) {
				if (this.props.searchErrors.errors && this.props.searchErrors.errors.suggestedFeeds !== undefined) {
					searchResultList = searchResultList.concat(this.props.searchErrors.errors.suggestedFeeds.feeds);
				}
			}

			if (searchResultList.length) {
				searchResults = (
					<div className="search-results">
						<ul className="search-results-list">
							{searchResultList.map((result, i) => {
								return (
									<FeedSearchResult
										key={i}
										result={result}
					 					selectFeed={this.selectFeed}/>
								);
							})}
						</ul>
						{searchTags}
					</div>
				);
			}
		}


		if (this.props.app.flags.routeViewType == constants.ROUTE_VIEW_ADVANCED || this.props.context === 'curate') {


			/*

			*/

			socialInputs = (
				<div className="input-connect-buttons">
					<div className="social-section-header">
						Or reshare Posts, Reels, and Stories from a profile or page.
					</div>
					<div className="tile-wrapper x-small complex">
						<SocialButtonConnect
							{...this.props}
							beforeClick={this.props.onSocialClick}
							service={constants.INPUT_TYPE_INSTAGRAM_BUSINESS}
							icon={`${imgPath}svg-logos/instagram-purp.svg`}
							recordPath={this.props.recordPath}
							router={this.props.router}
							serviceLabel={"Connect Instagram"}
							context="automate" />
						<SocialButtonConnect
							{...this.props}
							beforeClick={this.props.onSocialClick}
							service={constants.INPUT_TYPE_FACEBOOK_PAGE}
							icon={`${imgPath}svg-logos/facebook.svg`}
							recordPath={this.props.recordPath}
							router={this.props.router}
							serviceLabel={"Connect Facebook"}
							context="automate" />
						<SocialButtonConnect
							{...this.props}
							beforeClick={this.props.onSocialClick}
							service={constants.INPUT_TYPE_TIKTOK}
							icon={"ds-icon icon-tiktok"}
							recordPath={this.props.recordPath}
							router={this.props.router}
							serviceLabel={"Connect TikTok"}
							context="automate" />
						<SocialButtonConnect
							{...this.props}
							beforeClick={this.props.onSocialClick}
							service={constants.INPUT_TYPE_YOUTUBE}
							icon={"icon ion-social-youtube"}
							recordPath={this.props.recordPath}
							router={this.props.router}
							serviceLabel={"Connect YouTube"}
							context="automate" />
					</div>
				</div>
			);

		}

		let focusHandler = null;
		if (this.props.user.detail.routeViewType === constants.ROUTE_VIEW_ADVANCED) {
			focusHandler = this.focusHandler;
		}

		let searchControl = (
			<div>
				<Breakpoint desktop only>
					<div className="search-controls">
						<SearchControl
							{...this.props}
							extraClasses="text-input feed"
							placeholder={placeholderCopy}
							wrapperClass={searchWrapperClass}
							fieldIcon="icon ion-search"
							changeHandler={this.inputHandler}
							runOnFocus={false}
							focusHandler={focusHandler}
							defaultFocus={this.state.defaultFocus}
							fieldValue={this.state.searchQuery}
							trackReset={true} />
						{languageSelector}
					</div>
				</Breakpoint>
				<Breakpoint mobile only>
					<div className="search-controls">
						<SearchControl
							{...this.props}
							extraClasses="text-input feed"
							placeholder={placeholderCopy}
							wrapperClass={searchWrapperClass}
							fieldIcon="icon ion-search"
							changeHandler={this.inputHandler}
							runOnFocus={false}
							focusHandler={focusHandler}
							defaultFocus={this.state.defaultFocus}
							fieldValue={this.state.searchQuery}
							trackReset={true} />
					</div>
				</Breakpoint>
			</div>
		);


		if (this.props.app.flags.routeViewType == constants.ROUTE_VIEW_SIMPLE && this.state.activeTab === "suggested") {
			searchControl = (
				<SuggestedFeeds
					{...this.props}
					selectFeed={this.selectFeed}/>
			);
		}

		// hide the search control for simple if they have a feed set up
		if (this.props.app.flags.routeViewType == constants.ROUTE_VIEW_SIMPLE && this.props.initialFeeds.length >= 1) {
			searchControl = null;
		}

		return(
			<div>
				<div className="feed-search">
					<div className="editor-section-heading">
						{this.props.app.flags.routeViewType === constants.ROUTE_VIEW_SIMPLE && this.props.context !== "curate" ? (
							<div>
								<div className="input-type-display">
									{(this.props.inputType === 'wordpress') ? (
										<div><i className="icon ion-social-wordpress"/> Connect Wordpress Feed</div>
									) : (
										<div><i className="icon ion-social-rss"/> Connect RSS Feed</div>
									)}
								</div>
								<div className="feed-type-tabs">
									<div className={"feed-type-tab" + (this.state.activeTab === "rss" ? " selected" : "")} data-tab="rss" onClick={this.onTabClick}>
										Find RSS feed
									</div>
									<div className={"feed-type-tab" + (this.state.activeTab === "suggested" ? " selected" : "")} data-tab="suggested" onClick={this.onTabClick}>
										Explore Feeds
</div>
								</div>
							</div>

						) : (
							<div className="section-header">
								{titleCopy}
							</div>
						)}
					</div>
					{searchControl}
					{(this.props.initialFeeds.length) ? null : searchError}
					{suggestion}
					{socialInputs}
					{selectedFeeds}
					{searchResults}
				</div>
			</div>
		);
	}
};

CreateFeed.displayName = "CreateFeed";

CreateFeed.propTypes = {
	onFeedSelect: PropTypes.func,
	onFeedRemoval: PropTypes.func,
	initialFeeds: PropTypes.array,
	canAddFeeds: PropTypes.bool,
	onAdvancedFeedConfigure: PropTypes.func,
	onSocialClick: PropTypes.func,
	onPinterestClick: PropTypes.func,
	recordPath: PropTypes.func,
	context: PropTypes.string,
	searchErrors: PropTypes.oneOfType([
		PropTypes.object,
		PropTypes.array,
		PropTypes.string
	]),
	externalFeedUrl: PropTypes.string,
	/** Renders the new-design find step instead of the legacy search + tabs. */
	tile: PropTypes.bool
},

CreateFeed.defaultProps = {
	onFeedSelect: () => {},
	onFeedRemoval: () => {},
	initialFeeds: [],
	canAddFeeds: true,
	onAdvancedFeedConfigure: () => {},
	onSocialClick: () => {},
	onPinterestClick: () => {},
	recordPath: null,
	context: '',
	searchErrors: null,
	externalFeedUrl: '',
	tile: false
};

export default CreateFeed;
